import React from "react";

import { Box, Button, Typography, useMediaQuery } from "@mui/material";

import theme from "globals/design-system/theme";

type SimpleBanner = {
  type: 'simple';
  text: string;
  bgColor: string;
  textColor?: string;
  icon?: JSX.Element;
};

type ActionBanner = {
  type: 'action';
  text: string;
  bgColor: string;
  textColor?: string;
  icon?: JSX.Element;
  action: {
    text: string;
    onClick: () => void;
    startIcon?: JSX.Element;
    variant?: 'outlined';
  };
  secondaryAction?: {
    text: string;
    onClick: () => void;
    startIcon?: JSX.Element;
  };
};

export type MoovsBannerProps = SimpleBanner | ActionBanner;

function MoovsBanner(props: MoovsBannerProps) {
  // hooks
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
  });

  const renderActionButtons = () => {
    if (props.type !== 'action') return null;

    return (
      <Box
        display="flex"
        flexDirection={isMobileView ? "column" : "row-reverse"}
        gap={1}
        {...(isMobileView && { sx: { mt: 1 } })}
        {...(!!props.secondaryAction && {
          minWidth: "320px",
        })}
      >
        {/* Primary Action Button */}
        <Button
          color="primary"
          size={!!props.secondaryAction ? "small" : "medium"}
          variant={props.action.variant || "contained"}
          onClick={props.action.onClick}
          {...(!!props.action.startIcon && {
            startIcon: props.action.startIcon,
          })}
        >
          {props.action.text}
        </Button>

        {/* Secondary Action Button */}
        {renderSecondaryButton()}
      </Box>
    );
  };

  const renderSecondaryButton = () => {
    if (props.type !== 'action' || !props.secondaryAction) return null;

    return (
      <Button
        color="primary"
        size="small"
        variant="text"
        onClick={props.secondaryAction.onClick}
        {...(!!props.secondaryAction.startIcon && {
          startIcon: props.secondaryAction.startIcon,
        })}
      >
        {props.secondaryAction.text}
      </Button>
    );
  };

  return (
    <Box
      display="flex"
      flex="1"
      flexDirection={isMobileView ? "column" : "row"}
      px={3}
      py={2}
      borderRadius="4px"
      bgcolor={props.bgColor}
      alignItems="center"
      justifyContent="space-between"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {!!props.icon && <Box mr={2}>{props.icon}</Box>}
        <Typography
          variant={props.type === 'action' && props.secondaryAction ? "subtitle2" : "subtitle1"}
          {...(!!props.textColor && { style: { color: props.textColor } })}
        >
          {props.text}
        </Typography>
      </Box>

      {renderActionButtons()}
    </Box>
  );
}

export default MoovsBanner;
